import { useId, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../cn";

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  /** Shown under the hint, only while disabled — say why, not just that. */
  disabledBecause?: ReactNode;
  id?: string;
  className?: string;
}

/**
 * An on/off row: label and hint on the left, the track on the right.
 *
 * A native checkbox reads as "checkbox, checked" to a screen reader, which is
 * the wrong claim for a setting that takes effect the moment it is pressed
 * rather than one a form submits — `role="switch"` says that instead. The
 * whole row is the hit target, not just the track, for the same reason
 * `Checkbox`'s label is clickable: a knob the width of a track is not a
 * target anybody should have to aim for.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  disabledBecause,
  id,
  className,
}: SwitchProps) {
  const generated = useId();
  const switchId = id ?? generated;
  const hintId = `${switchId}-hint`;

  function toggle() {
    if (disabled) return;
    onChange(!checked);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      toggle();
    }
  }

  return (
    <div className={cn("flex w-full items-start justify-between gap-4", className)}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <label htmlFor={switchId} className="font-sans text-ui text-ink">
          {label}
        </label>
        {hint ? (
          <p id={hintId} className="text-meta text-ink-3">
            {hint}
          </p>
        ) : null}
        {disabled && disabledBecause ? (
          <p className="text-meta text-warning-text">{disabledBecause}</p>
        ) : null}
      </div>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={hint ? hintId : undefined}
        disabled={disabled}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className={cn(
          "relative inline-flex h-5.5 w-10 shrink-0 items-center rounded-full border transition-colors",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:pointer-events-none disabled:opacity-50",
          checked ? "border-accent bg-accent" : "border-rule-strong bg-surface-2",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "block size-4 rounded-full bg-surface shadow-[var(--shadow-card)] transition-transform",
            checked ? "translate-x-[1.25rem]" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}
